<script lang="ts">
  import type {DeleteAbortedClientInfo} from '$lib/models/integration/integration';
  import { Icons } from '$lib/components/common/icon';
  import IconButton from '$lib/components/common/iconButton/IconButton.svelte';
  import {dataSyncStore} from '$lib/stores/dataSyncStore';
  import type { GetAbortDeleteListResponse } from '$lib/models/alii-data-sync/types';
  import {onMount} from 'svelte';
  import {getDrawerStore, getToastStore, getModalStore} from '@skeletonlabs/skeleton';
  import { getErrorMessage, isErrorResponse } from '$lib/utils/error/errorUtils';
  import { showToast } from '$lib/helpers/toastrHelper';

  export let deleteAbortedClient: DeleteAbortedClientInfo;
  
  
  let abortDeleteItems: AbortDeleteListItem[] = [];

  const toastrStore = getToastStore();
  const drawerStore = getDrawerStore();
  const modalStore = getModalStore();

  let loading: boolean = true;
  let downloading: boolean = false;
  let pausing: boolean = false;
  let forcing: boolean = false;
  
  onMount(async () => {
    await loadData();
  });
  
  async function loadData() {
    loading = true;
    try {
      const result: GetAbortDeleteListResponse | ErrorResponse = await dataSyncStore.fetchAbortedClientEntities(deleteAbortedClient.clientId);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
      } else {
        const data = result as GetAbortDeleteListResponse;
        abortDeleteItems = data.data;
      }
    } catch (error) {
      console.error('Failed to load aborted entities:', error);
      showToast({
        title: 'Error',
        message: 'Failed to load aborted entities',
        type: 'error',
      });
    } finally {
      loading = false;
    }  
  }

  function onCloseClick() {
    drawerStore.close();
  }
  
  async function download(abortDeleteItem: AbortDeleteListItem) {
    if (downloading) return; // Prevent multiple clicks while downloading
    downloading = true;
    try {
      const result: void | ErrorResponse = await dataSyncStore.download(abortDeleteItem.clientId, abortDeleteItem.entityId, abortDeleteItem.syncType);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
      }
    } catch (error) {
      console.error('Failed to download aborted sync:', error);
      showToast({
        title: 'Error',
        message: 'Failed to download aborted sync',
        type: 'error',
      });
    } finally {
      downloading = false;
    }
  }

  function showPauseConfirm(abortDeleteItem: AbortDeleteListItem) {
    if (pausing) return; // Prevent multiple clicks while pausing
    if (abortDeleteItem.pauseAlertUntilLocal) {
      showToast({ title: 'Alert Paused', message: `Alerts for entity ${abortDeleteItem.entityName} {abortDeleteItem.syncType} are already paused until ${abortDeleteItem.pauseAlertUntilLocal}`, type: 'info' });
      return;
    }
    modalStore.trigger({
      type: 'confirm',
      title: 'Pause Alert',
      body: `Are you sure you want to pause alerts for the entity "${abortDeleteItem.entityName}" with sync type "${abortDeleteItem.syncType}"?`,
      response: (confirmed: boolean) => {
        if (confirmed) {
          pauseAlert(abortDeleteItem);
        }
      }
    });
  }

  async function pauseAlert(abortDeleteItem: AbortDeleteListItem) {
    if (pausing) return; // Prevent multiple clicks while pausing
    if (abortDeleteItem.pauseAlertUntilLocal) {
      showToast({ title: 'Alert Paused', message: `Alerts for entity ${abortDeleteItem.entityName} {abortDeleteItem.syncType} are already paused until ${abortDeleteItem.pauseAlertUntilLocal}`, type: 'info' });
      return;
    }
    pausing = true;
    try {
      const result: void | ErrorResponse = await dataSyncStore.pause(abortDeleteItem.clientId, abortDeleteItem.entityId, abortDeleteItem.syncType);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
      } else {
        showToast({ 
          title: 'Success', 
          message: `Entity ${abortDeleteItem.entityName} ${abortDeleteItem.syncType} has been paused successfully`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Failed to pause aborted sync:', error);
      showToast({
        title: 'Error',
        message: 'Failed to pause aborted sync',
        type: 'error',
      });
    } finally {
      pausing = false;
      loading = true;
      loadData();
    }
  }

  function showForceConfirm(abortDeleteItem: AbortDeleteListItem) {
    if (forcing) return; // Prevent multiple clicks while forcing
    modalStore.trigger({
      type: 'confirm',
      title: 'Force Sync',
      body: `Are you sure you want to force sync for the entity "${abortDeleteItem.entityName}" with sync type "${abortDeleteItem.syncType}"?`,
      response: (confirmed: boolean) => {
        if (confirmed) {
          forceSync(abortDeleteItem);
        }
      }
    });
  }

  async function forceSync(abortDeleteItem: AbortDeleteListItem) {
    if (forcing) return; // Prevent multiple clicks while forcing
    forcing = true;
    try {
      const result: void | ErrorResponse = await dataSyncStore.force(abortDeleteItem.clientId, abortDeleteItem.entityId, abortDeleteItem.syncType);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
      } else {
        showToast({ 
          title: 'Success', 
          message: `Entity ${abortDeleteItem.entityName} ${abortDeleteItem.syncType} has been forced successfully`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Failed to force aborted sync:', error);
      showToast({
        title: 'Error',
        message: 'Failed to force aborted sync',
        type: 'error',
      });
    } finally {
      forcing = false;
      loading = true;
      await loadData();
    }
  }

</script>

<div class="pb-4 px-4">
  <div class="sticky top-0 bg-white z-10 pt-5">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div>
          <div class="text-base font-semibold text-gray-900 leading-tight">Current Delete Aborted Entity Syncs</div>
          <div class="text-sm text-gray-500 leading-none">{deleteAbortedClient.clientName}</div>
        </div>
      </div>
      <IconButton icon="x" onClick={drawerStore.close} />
    </div>

    <hr class="divider mt-5" />
  </div>

    <div class="bg-gray-50 border rounded-lg p-4 overflow-auto">
      {#if loading}
        <pre class="text-xs whitespace-pre-wrap text-gray-800">Loading details please wait</pre>
      {:else}
        <pre class="text-xs whitespace-pre-wrap text-gray-800 font-mono">
          {#if abortDeleteItems.length > 0}
            <div class="overflow-x-auto max-h-128 overflow-y-auto">
                <table class="table table-hover">
                    <thead>
                        <tr>
                          <th class="!text-md h-[39px] w-[5%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Entity Id</th>
                          <th class="!text-md h-[39px] w-[20%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Entity Name</th>
                          <th class="!text-md h-[39px] w-[10%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Sync Type</th>
                          <th class="!text-md h-[39px] w-[10%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Timestamp</th>
                          <th class="!text-md h-[39px] w-[10%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Page Count</th>
                          <th class="!text-md h-[39px] w-[10%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Pause Alerts Until</th>
                          <th class="!text-md h-[39px] w-[20%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Reason</th>
                          <th class="!text-md h-[39px] w-[5%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">CSV</th>
                          <th class="!text-md h-[39px] w-[5%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Pause</th>
                          <th class="!text-md h-[39px] w-[5%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]">Force</th>
                        </tr>
                    </thead>
                    <tbody>
                      {#each abortDeleteItems as abortDeleteItem}
                        {@const pauseIconClass = abortDeleteItem.pauseAlertUntilLocal ? "text-error-500" : ""}
                        {@const pauseTitle = abortDeleteItem.pauseAlertUntilLocal ? "Alert is paused" : "Pause the alert"}
                        <tr>
                          <td class="h-10 w-[5%] content-center !px-5">{abortDeleteItem.entityId}</td>
                          <td class="h-10 w-[20%] content-center !px-5">{abortDeleteItem.entityName}</td>
                          <td class="h-10 w-[10%] content-center !px-5">{abortDeleteItem.syncType}</td>
                          <td class="h-10 w-[10%] content-center !px-5">{abortDeleteItem.timestampLocal}</td>
                          <td class="h-10 w-[10%] content-center !px-5">{abortDeleteItem.totalPageCount}</td>
                          <td class="h-10 w-[10%] content-center !px-5">{abortDeleteItem.pauseAlertUntilLocal}</td>
                          <td class="h-10 w-[20%] content-center !px-5">{abortDeleteItem.abortReason}</td>
                          <td class="h-10 w-[5%] content-center !px-5">
                            <div>
                              <IconButton icon="download" displayBorder={"true"} title={"Download CSV of the items to delete"} onClick={() => download(abortDeleteItem)} />
                            </div>
                          </td>
                          <td class="h-10 w-[5%] content-center !px-5">
                            <div>
                              <IconButton icon="notification" iconClass={pauseIconClass} displayBorder={"true"} title={pauseTitle} onClick={() => showPauseConfirm(abortDeleteItem)} />
                            </div>
                          </td>
                          <td class="h-10 w-[5%] content-center !px-5">
                            <div>
                              <IconButton icon="check" displayBorder={"true"} title={"Force the Sync"} onClick={() => showForceConfirm(abortDeleteItem)} />
                            </div>
                          </td>
                        </tr>
                      {/each}
                    </tbody>
                </table>
            </div>
          {:else}
            <div class="flex flex-col items-center justify-center py-10 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-red-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L4.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 class="text-lg font-semibold text-red-600">There are no Delete Aborted Entity Syncs for this client</h3>
            </div>
          {/if}
        </pre>
      {/if}
    </div>


</div>